import type { ReactNode } from 'react';
import { ArrowRight, ArrowUpRight, BriefcaseBusiness, Building2, ChevronRight, Database, Globe2, MapPin } from 'lucide-react';
import styles from './JobCardLabPage.module.css';

type Channel = 'linkedin' | 'indeed';
type SourcePool = 'ats' | 'global' | 'company';

const CHANNEL_LOGO: Record<Channel, { src: string; alt: string }> = {
    linkedin: { src: 'logo_linkedin.svg', alt: 'LinkedIn' },
    indeed: { src: 'logo_indeed.svg', alt: 'Indeed' },
};

const POOL_META: Record<SourcePool, { label: string; Icon: typeof Database }> = {
    ats: { label: 'ATS', Icon: Database },
    global: { label: 'Global', Icon: Globe2 },
    company: { label: 'Talent Pool', Icon: Building2 },
};

type Fixture = {
    title: string;
    department?: string;
    location?: string;
    candidates: number;
    automations: number;
    state: 'receiving' | 'sourcing' | 'paused' | 'drafting';
    pendingReview: number;
    channels: Channel[];
    pools: SourcePool[];
};

const FIXTURES: Fixture[] = [
    {
        title: 'Senior Backend Engineer, Payments',
        department: 'Engineering',
        location: 'Remote US',
        candidates: 47,
        automations: 3,
        state: 'receiving',
        pendingReview: 5,
        channels: ['linkedin', 'indeed'],
        pools: [],
    },
    {
        title: 'Senior Product Designer, Checkout',
        department: 'Design',
        location: 'Remote',
        candidates: 12,
        automations: 4,
        state: 'sourcing',
        pendingReview: 0,
        channels: [],
        pools: ['global', 'company'],
    },
    {
        title: 'Senior Data Engineer',
        location: 'Remote',
        candidates: 0,
        automations: 2,
        state: 'receiving',
        pendingReview: 0,
        channels: ['linkedin'],
        pools: [],
    },
    {
        title: 'Tighten Pipeline to 3 Stages',
        candidates: 0,
        automations: 0,
        state: 'drafting',
        pendingReview: 0,
        channels: [],
        pools: [],
    },
];

function ChannelLogos({ channels, size = 14 }: { channels: Channel[]; size?: number }) {
    if (channels.length === 0) return null;
    return (
        <span className={styles.channelLogos} style={{ ['--logo-size' as string]: `${size}px` }}>
            {channels.map((c) => (
                <img key={c} src={`/assets/icons/${CHANNEL_LOGO[c].src}`} alt={CHANNEL_LOGO[c].alt} loading="lazy" />
            ))}
        </span>
    );
}

function SourcePools({ pools, size = 12 }: { pools: SourcePool[]; size?: number }) {
    if (pools.length === 0) return null;
    return (
        <span className={styles.poolList}>
            {pools.map((p, i) => {
                const { label, Icon } = POOL_META[p];
                return (
                    <span key={p} className={styles.poolItem}>
                        <Icon size={size} strokeWidth={1.85} />
                        {label}
                        {i < pools.length - 1 ? <span className={styles.poolSep}>,</span> : null}
                    </span>
                );
            })}
        </span>
    );
}

export function JobCardLabPage() {
    return (
        <div className={styles.page}>
            <header className={styles.pageHeader}>
                <h1>Job card lab</h1>
                <p>Six layout variations for the /jobs list. Same fixtures rendered in each. Pick what reads fastest.</p>
            </header>

            <Variant
                label="A — Current"
                note="Title alone, status chips on meta row. Baseline."
                Card={CardA}
            />
            <Variant
                label="B — Eyebrow chip on top, with action count"
                note="Status promoted to a kicker line above the title. Pulse means inbound is live. Count lives in the same line."
                Card={CardB}
            />
            <Variant
                label="C — Eyebrow + right-rail action count"
                note="Status on top-left, action count on top-right. Title gets full width."
                Card={CardC}
            />
            <Variant
                label="D — Pulsing dot leading the title (no chip)"
                note="Minimal: a single colored dot left of the title encodes state. Channels demoted to text."
                Card={CardD}
            />
            <Variant
                label="E — Two-channel state, sourcing as a peer"
                note="Inbound and Sourcing are both first-class signals with their own colors and pulses."
                Card={CardE}
            />
            <Variant
                label="F — Action-first, status secondary"
                note="The pending-review count is the loudest element. Status moves into the meta line."
                Card={CardF}
            />
            <Variant
                label="G — Refined: big pulse on top, no chips"
                note="F's structure, but the inbound/sourcing line moves to the top with a bigger pulsing dot. Review count sits next to it. Title is clean. Meta is just dept and location."
                Card={CardG}
            />
            <Variant
                label="G1 — Title is the link"
                note="Minimal change. Title text gets underline-on-hover and pointer cursor. Click anywhere on the title to enter the role."
                Card={CardG1}
            />
            <Variant
                label="G2 — Whole card is clickable"
                note="Card hover lifts the border and shows a chevron on the far right. Click anywhere outside interactive controls to enter."
                Card={CardG2}
            />
            <Variant
                label="G3 — Explicit Open button"
                note="A clear 'Open role →' CTA next to the counts. Most discoverable, reads as an obvious affordance."
                Card={CardG3}
            />
            <Variant
                label="G4 — Trailing chevron rail"
                note="A subtle chevron lives at the far right of the card; on hover it brightens and the card border emphasises. Common list-row pattern."
                Card={CardG4}
            />
        </div>
    );
}

function Variant({ label, note, Card }: { label: string; note: string; Card: (props: { fixture: Fixture }) => ReactNode }) {
    return (
        <section className={styles.variant}>
            <div className={styles.variantHeader}>
                <h2>{label}</h2>
                <p>{note}</p>
            </div>
            <div className={styles.cardList}>
                {FIXTURES.map((f) => (
                    <Card key={f.title + label} fixture={f} />
                ))}
            </div>
        </section>
    );
}

// ─── A: current ───────────────────────────────────────────────────────────
function CardA({ fixture }: { fixture: Fixture }) {
    return (
        <article className={styles.card}>
            <div className={styles.cardMain}>
                <h3 className={styles.title}>{fixture.title}</h3>
                <div className={styles.metaLine}>
                    {fixture.department ? (
                        <span className={styles.metaItem}>
                            <BriefcaseBusiness size={12} strokeWidth={1.75} />
                            {fixture.department}
                        </span>
                    ) : null}
                    {fixture.location ? (
                        <span className={styles.metaItem}>
                            <MapPin size={12} strokeWidth={1.75} />
                            {fixture.location}
                        </span>
                    ) : null}
                    <StatusChip state={fixture.state} pulse={false} channels={fixture.channels} pools={fixture.pools} />
                </div>
            </div>
            <RightStats fixture={fixture} />
        </article>
    );
}

// ─── B: eyebrow on top, count inline ──────────────────────────────────────
function CardB({ fixture }: { fixture: Fixture }) {
    return (
        <article className={styles.card}>
            <div className={styles.cardMain}>
                <div className={styles.eyebrow}>
                    <StatusChip state={fixture.state} pulse channels={fixture.channels} pools={fixture.pools} />
                    {fixture.pendingReview > 0 ? (
                        <>
                            <span className={styles.eyebrowDot}>·</span>
                            <span className={styles.actionInline}>{fixture.pendingReview} to review</span>
                        </>
                    ) : null}
                </div>
                <h3 className={styles.title}>{fixture.title}</h3>
                <div className={styles.metaLine}>
                    {fixture.department ? (
                        <span className={styles.metaItem}>
                            <BriefcaseBusiness size={12} strokeWidth={1.75} />
                            {fixture.department}
                        </span>
                    ) : null}
                    {fixture.location ? (
                        <span className={styles.metaItem}>
                            <MapPin size={12} strokeWidth={1.75} />
                            {fixture.location}
                        </span>
                    ) : null}
                </div>
            </div>
            <RightStats fixture={fixture} />
        </article>
    );
}

// ─── C: eyebrow left, action right ────────────────────────────────────────
function CardC({ fixture }: { fixture: Fixture }) {
    return (
        <article className={styles.card}>
            <div className={styles.cardMain}>
                <div className={styles.eyebrowRow}>
                    <div className={styles.eyebrow}>
                        <StatusChip state={fixture.state} pulse channels={fixture.channels} pools={fixture.pools} />
                    </div>
                    {fixture.pendingReview > 0 ? <ActionPill count={fixture.pendingReview} /> : null}
                </div>
                <h3 className={styles.title}>{fixture.title}</h3>
                <div className={styles.metaLine}>
                    {fixture.department ? (
                        <span className={styles.metaItem}>
                            <BriefcaseBusiness size={12} strokeWidth={1.75} />
                            {fixture.department}
                        </span>
                    ) : null}
                    {fixture.location ? (
                        <span className={styles.metaItem}>
                            <MapPin size={12} strokeWidth={1.75} />
                            {fixture.location}
                        </span>
                    ) : null}
                </div>
            </div>
            <RightStats fixture={fixture} />
        </article>
    );
}

// ─── D: leading dot, no chip ──────────────────────────────────────────────
function CardD({ fixture }: { fixture: Fixture }) {
    return (
        <article className={styles.card}>
            <div className={styles.cardMain}>
                <div className={styles.titleWithDot}>
                    <StateDot state={fixture.state} pulse />
                    <h3 className={styles.title}>{fixture.title}</h3>
                </div>
                <div className={styles.metaLine}>
                    <span className={`${styles.metaItem} ${styles.metaState}`}>
                        <StateText state={fixture.state} channels={fixture.channels} pools={fixture.pools} />
                    </span>
                    {fixture.department ? (
                        <>
                            <span className={styles.metaDot}>·</span>
                            <span className={styles.metaItem}>
                                <BriefcaseBusiness size={12} strokeWidth={1.75} />
                                {fixture.department}
                            </span>
                        </>
                    ) : null}
                    {fixture.location ? (
                        <>
                            <span className={styles.metaDot}>·</span>
                            <span className={styles.metaItem}>
                                <MapPin size={12} strokeWidth={1.75} />
                                {fixture.location}
                            </span>
                        </>
                    ) : null}
                </div>
            </div>
            <RightStats fixture={fixture} action={fixture.pendingReview} />
        </article>
    );
}

// ─── E: two parallel state pulses ─────────────────────────────────────────
function CardE({ fixture }: { fixture: Fixture }) {
    const inboundActive = fixture.state === 'receiving';
    const sourcingActive = fixture.state === 'sourcing' || fixture.pools.length > 0;

    return (
        <article className={styles.card}>
            <div className={styles.cardMain}>
                <div className={styles.eyebrow}>
                    {inboundActive ? <StatusChip state="receiving" pulse compact channels={fixture.channels} pools={fixture.pools} /> : null}
                    {sourcingActive ? <StatusChip state="sourcing" pulse compact channels={fixture.channels} pools={fixture.pools} /> : null}
                    {!inboundActive && !sourcingActive ? <StatusChip state={fixture.state} pulse={false} /> : null}
                    {fixture.pendingReview > 0 ? (
                        <>
                            <span className={styles.eyebrowDot}>·</span>
                            <span className={styles.actionInline}>{fixture.pendingReview} to review</span>
                        </>
                    ) : null}
                </div>
                <h3 className={styles.title}>{fixture.title}</h3>
                <div className={styles.metaLine}>
                    {fixture.department ? (
                        <span className={styles.metaItem}>
                            <BriefcaseBusiness size={12} strokeWidth={1.75} />
                            {fixture.department}
                        </span>
                    ) : null}
                    {fixture.location ? (
                        <span className={styles.metaItem}>
                            <MapPin size={12} strokeWidth={1.75} />
                            {fixture.location}
                        </span>
                    ) : null}
                </div>
            </div>
            <RightStats fixture={fixture} />
        </article>
    );
}

// ─── F: action first ──────────────────────────────────────────────────────
function CardF({ fixture }: { fixture: Fixture }) {
    return (
        <article className={styles.card}>
            <div className={styles.cardMain}>
                {fixture.pendingReview > 0 ? <div className={styles.actionLoud}>{fixture.pendingReview} to review</div> : null}
                <h3 className={styles.title}>{fixture.title}</h3>
                <div className={styles.metaLine}>
                    <span className={styles.metaItem}>
                        <StateDot state={fixture.state} pulse size="small" />
                        <StateText state={fixture.state} channels={fixture.channels} pools={fixture.pools} />
                    </span>
                    {fixture.department ? (
                        <span className={styles.metaItem}>
                            <BriefcaseBusiness size={12} strokeWidth={1.75} />
                            {fixture.department}
                        </span>
                    ) : null}
                    {fixture.location ? (
                        <span className={styles.metaItem}>
                            <MapPin size={12} strokeWidth={1.75} />
                            {fixture.location}
                        </span>
                    ) : null}
                </div>
            </div>
            <RightStats fixture={fixture} />
        </article>
    );
}

// ─── G: refined — big pulse on top, no chip ───────────────────────────────
function CardG({ fixture }: { fixture: Fixture }) {
    const isActive = fixture.state === 'receiving' || fixture.state === 'sourcing';
    return (
        <article className={styles.card}>
            <div className={styles.cardMain}>
                <div className={styles.gEyebrow}>
                    <span className={styles.gStatus}>
                        <StateDot state={fixture.state} pulse={isActive} size="large" />
                        <StateText state={fixture.state} channels={fixture.channels} pools={fixture.pools} />
                    </span>
                    {fixture.pendingReview > 0 ? (
                        <>
                            <span className={styles.eyebrowDot}>·</span>
                            <span className={styles.actionInline}>{fixture.pendingReview} to review</span>
                        </>
                    ) : null}
                </div>
                <h3 className={styles.title}>{fixture.title}</h3>
                <div className={styles.metaLine}>
                    {fixture.department ? (
                        <span className={styles.metaItem}>
                            <BriefcaseBusiness size={12} strokeWidth={1.75} />
                            {fixture.department}
                        </span>
                    ) : null}
                    {fixture.location ? (
                        <span className={styles.metaItem}>
                            <MapPin size={12} strokeWidth={1.75} />
                            {fixture.location}
                        </span>
                    ) : null}
                </div>
            </div>
            <RightStats fixture={fixture} />
        </article>
    );
}

function GBody({ fixture, titleClassName }: { fixture: Fixture; titleClassName?: string }) {
    const isActive = fixture.state === 'receiving' || fixture.state === 'sourcing';
    return (
        <>
            <div className={styles.gEyebrow}>
                <span className={styles.gStatus}>
                    <StateDot state={fixture.state} pulse={isActive} size="large" />
                    <StateText state={fixture.state} channels={fixture.channels} pools={fixture.pools} />
                </span>
                {fixture.pendingReview > 0 ? (
                    <>
                        <span className={styles.eyebrowDot}>·</span>
                        <span className={styles.actionInline}>{fixture.pendingReview} to review</span>
                    </>
                ) : null}
            </div>
            <h3 className={`${styles.title} ${titleClassName ?? ''}`}>{fixture.title}</h3>
            <div className={styles.metaLine}>
                {fixture.department ? (
                    <span className={styles.metaItem}>
                        <BriefcaseBusiness size={12} strokeWidth={1.75} />
                        {fixture.department}
                    </span>
                ) : null}
                {fixture.location ? (
                    <span className={styles.metaItem}>
                        <MapPin size={12} strokeWidth={1.75} />
                        {fixture.location}
                    </span>
                ) : null}
            </div>
        </>
    );
}

// ─── G1: title is the link ────────────────────────────────────────────────
function CardG1({ fixture }: { fixture: Fixture }) {
    return (
        <article className={styles.card}>
            <div className={styles.cardMain}>
                <GBody fixture={fixture} titleClassName={styles.titleLink} />
            </div>
            <RightStats fixture={fixture} />
        </article>
    );
}

// ─── G2: whole card is clickable ──────────────────────────────────────────
function CardG2({ fixture }: { fixture: Fixture }) {
    return (
        <article className={`${styles.card} ${styles.cardClickable}`}>
            <div className={styles.cardMain}>
                <GBody fixture={fixture} />
            </div>
            <RightStats fixture={fixture} />
            <span className={styles.cardChevron} aria-hidden="true">
                <ChevronRight size={18} strokeWidth={1.75} />
            </span>
        </article>
    );
}

// ─── G3: explicit Open button ─────────────────────────────────────────────
function CardG3({ fixture }: { fixture: Fixture }) {
    return (
        <article className={styles.card}>
            <div className={styles.cardMain}>
                <GBody fixture={fixture} />
            </div>
            <div className={styles.cardAside}>
                <RightStats fixture={fixture} />
                <button type="button" className={styles.openButton}>
                    Open role
                    <ArrowRight size={14} strokeWidth={2} />
                </button>
            </div>
        </article>
    );
}

// ─── G4: trailing chevron rail ────────────────────────────────────────────
function CardG4({ fixture }: { fixture: Fixture }) {
    return (
        <article className={`${styles.card} ${styles.cardRail}`}>
            <div className={styles.cardMain}>
                <GBody fixture={fixture} />
            </div>
            <RightStats fixture={fixture} />
            <span className={styles.cardRailChevron} aria-hidden="true">
                <ChevronRight size={20} strokeWidth={1.6} />
            </span>
        </article>
    );
}

// ─── shared bits ──────────────────────────────────────────────────────────

function StatusChip({
    state,
    pulse,
    channels = [],
    pools = [],
    compact = false,
}: {
    state: Fixture['state'];
    pulse: boolean;
    channels?: Channel[];
    pools?: SourcePool[];
    compact?: boolean;
}) {
    const tone = state === 'receiving' ? 'green' : state === 'sourcing' ? 'blue' : state === 'paused' ? 'gray' : 'mute';
    return (
        <span className={`${styles.chip} ${styles[`chip-${tone}`]} ${compact ? styles.chipCompact : ''}`}>
            <StateDot state={state} pulse={pulse} size="small" />
            {state === 'receiving' && channels.length > 0 ? (
                <>
                    <span>Posted to</span>
                    <ChannelLogos channels={channels} size={12} />
                </>
            ) : state === 'sourcing' && pools.length > 0 ? (
                <>
                    <span>Sourcing from</span>
                    <SourcePools pools={pools} size={12} />
                </>
            ) : (
                stateLabel(state)
            )}
        </span>
    );
}

function StateDot({ state, pulse, size = 'default' }: { state: Fixture['state']; pulse: boolean; size?: 'small' | 'default' | 'large' }) {
    const tone = state === 'receiving' ? 'green' : state === 'sourcing' ? 'blue' : state === 'paused' ? 'gray' : 'mute';
    const sizeClass = size === 'small' ? styles.dotSmall : size === 'large' ? styles.dotLarge : '';
    return (
        <span
            className={`${styles.dot} ${styles[`dot-${tone}`]} ${pulse ? styles.dotPulse : ''} ${sizeClass}`}
            aria-hidden="true"
        />
    );
}

function StateText({ state, channels, pools }: { state: Fixture['state']; channels: Channel[]; pools: SourcePool[] }) {
    if (state === 'receiving' && channels.length > 0) {
        return (
            <>
                <span>Posted to</span>
                <ChannelLogos channels={channels} size={12} />
            </>
        );
    }
    if (state === 'sourcing' && pools.length > 0) {
        return (
            <>
                <span>Sourcing from</span>
                <SourcePools pools={pools} size={12} />
            </>
        );
    }
    return <>{stateLabel(state)}</>;
}

function ActionPill({ count }: { count: number }) {
    return <span className={styles.actionPill}>{count} to review <ArrowUpRight size={12} strokeWidth={2} /></span>;
}

function RightStats({ fixture, action }: { fixture: Fixture; action?: number }) {
    return (
        <div className={styles.rightStats}>
            {action && action > 0 ? <span className={styles.rightAction}>{action} to review</span> : null}
            <span><strong>{fixture.candidates}</strong> candidates</span>
            <span className={styles.rightDot}>·</span>
            <span><strong>{fixture.automations}</strong> automations</span>
        </div>
    );
}

function stateLabel(state: Fixture['state']): string {
    if (state === 'receiving') return 'Receiving applications';
    if (state === 'sourcing') return 'Sourcing';
    if (state === 'paused') return 'Paused';
    return 'Drafting';
}
